import { useEffect } from "react";
import { toast } from "react-toastify";
import { FiAlertTriangle } from "react-icons/fi";
import "./ContentProtection.css";

export default function useContentProtection() {
  useEffect(() => {
    let selecting = false;
    let warnCount = 0;
    const MAX_WARNINGS = 3;
    const INACTIVITY_LIMIT = 3 * 60 * 1000; // 5 minutes
    let inactivityTimer;

    const resetWarnings = () => {
      warnCount = 0;
    };

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(resetWarnings, INACTIVITY_LIMIT);
    };

    const showWarning = () => {
      if (warnCount >= MAX_WARNINGS) return; // stop after 3 warnings
      warnCount++;

      toast.dismiss();
      toast(
        <div className="protection-toast">
          <FiAlertTriangle className="protection-icon" />
          <span>Content is protected — copying is disabled.</span>
        </div>,
        {
          autoClose: 1500,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: false,
          draggable: false,
          className: "protection-toast-container",
        }
      );
    };

    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable === true;
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0 || isEditable(e.target)) return;
      selecting = true;
      resetInactivityTimer();
    };

    const handleMouseUp = (e) => {
      if (selecting && !isEditable(e.target)) {
        const selection = window.getSelection ? window.getSelection().toString() : "";
        if (selection && selection.length > 2) {
          showWarning();
          window.getSelection().removeAllRanges();
        }
      }
      selecting = false;
      resetInactivityTimer();
    };

    const handleCopy = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
      showWarning();
      resetInactivityTimer();
    };

    const handleContextMenu = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
      showWarning();
      resetInactivityTimer();
    };

    const handleKeyDown = (e) => {
      const key = e.key && typeof e.key === "string" ? e.key.toLowerCase() : "";
      if (isEditable(e.target)) return;
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "s", "u", "p"].includes(key)) {
        e.preventDefault();
        showWarning();
      }
      resetInactivityTimer();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    resetInactivityTimer(); // start timer when component mounts

    return () => {
      clearTimeout(inactivityTimer);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
}
